import { FormContent, FormFields, PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { FieldGroup, FieldLegend, FieldSet } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CaseWorkspace, { CaseFacts } from "@/components/case-workspace";
import { ConfirmSubmitButton } from "@/components/confirm-submit-button";
import { FormErrors, errorText } from "@/components/forms/form-errors";
import { useResourceDraft } from "@/hooks/use-resource-draft";
import {
  locationServiceArchivalPath,
  locationServicePath,
  locationServicesPath,
} from "@/routes";
import type { Service, Offering } from "./types";
import BookingSetupReturn, {
  BookingReturnField,
  type BookingReturnContext,
} from "@/components/booking-setup-return";

const offeringFields = [
  "price",
  "currency",
  "estimated_minutes",
  "tax_rate",
  "tax_label",
] as const;
const offeringTargets = {
  price_minor: "offering-price",
  tax_rate_parts_per_million: "offering-tax_rate",
  ...Object.fromEntries(
    [...offeringFields, "tax_mode", "available"].map((field) => [
      field,
      `offering-${field}`,
    ]),
  ),
};

export default function ServiceForm({
  service,
  offering = null,
  booking_return,
}: {
  service: Service | null;
  offering?: Offering | null;
  booking_return?: BookingReturnContext;
}) {
  const { auth, errors } = usePage().props;
  const { t } = useTranslation();
  const serviceDraft = useResourceDraft(
    {
      account: auth.account?.id || "",
      actor: auth.user?.id || "",
      location: auth.location?.id || "",
      kind: "service",
      record: service?.id || "new",
      revision: service?.revision || "new",
    },
    {
      name: service?.name || "",
      description: service?.description || "",
      offer_at_location: offering || !service || booking_return ? "1" : "0",
    },
  );
  const offeringDraft = useResourceDraft(
    {
      account: auth.account?.id || "",
      actor: auth.user?.id || "",
      location: auth.location?.id || "",
      kind: "offering",
      record: offering?.id || service?.id || "new",
      revision: offering?.revision || "new",
    },
    {
      price: offering?.price || "",
      currency: offering?.currency || auth.location?.currency || "",
      estimated_minutes: offering ? String(offering.estimated_minutes) : "",
      tax_rate: offering?.tax_rate || "0",
      tax_label: offering?.tax_label || "",
      tax_mode: offering?.tax_mode || "exclusive",
      available: offering?.available === false ? "0" : "1",
    },
  );
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const locationName = auth.location.name;
  const title = t(service ? "services.edit" : "services.add");
  const localOffering =
    Boolean(offering || booking_return) ||
    serviceDraft.values.offer_at_location !== "0";
  const draftStatus = [serviceDraft.status, offeringDraft.status];
  const status = draftStatus.includes("unavailable")
    ? "unavailable"
    : draftStatus.includes("restored")
      ? "restored"
      : draftStatus.includes("saved")
        ? "saved"
        : "";
  const discard = () => {
    serviceDraft.discard();
    offeringDraft.discard();
  };
  const offeringError = (field: string) =>
    errors?.[field] ||
    (field === "price"
      ? errors?.price_minor
      : field === "tax_rate"
        ? errors?.tax_rate_parts_per_million
        : undefined);
  const fieldError = (field: string) => {
    const message = offeringError(field);
    return message ? (
      <p id={`offering-${field}-error`} className="text-sm text-destructive">
        {errorText(message)}
      </p>
    ) : null;
  };
  return (
    <AppLayout
      breadcrumbs={[
        { title: t("services.title"), href: locationServicesPath(locationId) },
      ]}
    >
      <Head title={title} />
      <PageCanvas>
        <BookingSetupReturn context={booking_return} />
        <CaseWorkspace
          context={
            <CaseFacts
              facts={[
                {
                  label: t("resource_workspace.location"),
                  value: locationName,
                },
                ...(service
                  ? [{ label: t("services.name"), value: service.name }]
                  : []),
              ]}
            />
          }
        >
          <FormContent>
            <h1 className="text-page font-display">{title}</h1>
            <FormErrors
              errors={errors || {}}
              targets={{
                name: "service-name",
                description: "service-description",
                ...offeringTargets,
              }}
            />
            {status && (
              <p role="status" className="text-sm text-muted-foreground">
                {t(`resource_drafts.${status}`)}
              </p>
            )}
            <Form
              key={`${service?.revision || "new"}:${offering?.revision || "new"}`}
              onSuccess={discard}
              action={
                service
                  ? locationServicePath(locationId, service.id)
                  : locationServicesPath(locationId)
              }
              method={service ? "patch" : "post"}
            >
              {({ processing }) => (
                <div className="space-y-6">
                  <BookingReturnField context={booking_return} />
                  <input type="hidden" name="editor" value="service" />
                  <input
                    type="hidden"
                    name="offer_at_location"
                    value={localOffering ? "1" : "0"}
                  />
                  <input
                    type="hidden"
                    name="offering_revision"
                    value={offering?.revision || ""}
                  />
                  {service && (
                    <input
                      type="hidden"
                      name="revision"
                      value={service.revision}
                    />
                  )}
                  <fieldset disabled={processing} className="min-w-0">
                    <FieldGroup className="gap-8">
                      <FieldSet>
                        <FieldLegend variant="section">
                          {t("services.details_title")}
                        </FieldLegend>
                        <p className="text-sm text-muted-foreground">
                          {t("services.shared_help")}
                        </p>
                        <FormFields paired={false}>
                          <div className="space-y-2">
                            <Label htmlFor="service-name">
                              {t("services.name")}
                            </Label>
                            <Input
                              autoFocus
                              id="service-name"
                              name="service[name]"
                              value={serviceDraft.values.name}
                              onChange={(event) =>
                                serviceDraft.change("name", event.target.value)
                              }
                              maxLength={200}
                              aria-invalid={!!errors?.name}
                              aria-describedby={
                                errors?.name ? "service-name-error" : undefined
                              }
                            />
                            {errors?.name && (
                              <p
                                id="service-name-error"
                                className="text-sm text-destructive"
                              >
                                {errorText(errors.name)}
                              </p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="service-description">
                              {t("services.description")}
                            </Label>
                            <Textarea
                              id="service-description"
                              name="service[description]"
                              value={serviceDraft.values.description}
                              onChange={(event) =>
                                serviceDraft.change(
                                  "description",
                                  event.target.value,
                                )
                              }
                              maxLength={10000}
                              aria-invalid={!!errors?.description}
                              aria-describedby={
                                errors?.description
                                  ? "service-description-error"
                                  : undefined
                              }
                            />
                            {errors?.description && (
                              <p
                                id="service-description-error"
                                className="text-sm text-destructive"
                              >
                                {errorText(errors.description)}
                              </p>
                            )}
                          </div>
                        </FormFields>
                      </FieldSet>
                      <FieldSet className="border-t pt-6">
                        <FieldLegend variant="section">
                          {t("services.local_title", {
                            location: locationName,
                          })}
                        </FieldLegend>
                        <p className="text-sm text-muted-foreground">
                          {t("services.local_help", {
                            location: locationName,
                          })}
                        </p>
                        {!offering && (
                          <div className="space-y-2">
                            <div className="flex min-h-11 items-center gap-3">
                              <Checkbox
                                id="service-offer-at-location"
                                checked={localOffering}
                                disabled={Boolean(booking_return)}
                                onCheckedChange={(checked) =>
                                  serviceDraft.change(
                                    "offer_at_location",
                                    checked ? "1" : "0",
                                  )
                                }
                              />
                              <Label htmlFor="service-offer-at-location">
                                {t("services.offer_at_location", {
                                  location: locationName,
                                })}
                              </Label>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {t(
                                booking_return
                                  ? "services.local_required_help"
                                  : "services.local_optional_help",
                              )}
                            </p>
                          </div>
                        )}
                        <fieldset
                          className="min-w-0 space-y-5"
                          disabled={!localOffering}
                        >
                          <FormFields>
                            {offeringFields.map((field) => (
                              <div className="space-y-2" key={field}>
                                <Label htmlFor={`offering-${field}`}>
                                  {t(
                                    field === "price"
                                      ? "services.unit_price"
                                      : `services.${field}`,
                                  )}
                                </Label>
                                <Input
                                  id={`offering-${field}`}
                                  name={`service_offering[${field}]`}
                                  type={
                                    field === "estimated_minutes"
                                      ? "number"
                                      : "text"
                                  }
                                  min={
                                    field === "estimated_minutes"
                                      ? 1
                                      : undefined
                                  }
                                  step={
                                    field === "estimated_minutes"
                                      ? 1
                                      : undefined
                                  }
                                  inputMode={
                                    field === "price" || field === "tax_rate"
                                      ? "decimal"
                                      : undefined
                                  }
                                  maxLength={
                                    field === "tax_label" ? 100 : undefined
                                  }
                                  value={offeringDraft.values[field]}
                                  onChange={(event) =>
                                    offeringDraft.change(
                                      field,
                                      event.target.value,
                                    )
                                  }
                                  aria-invalid={!!offeringError(field)}
                                  aria-describedby={
                                    [
                                      field === "price" || field === "tax_rate"
                                        ? "offering-input-help"
                                        : "",
                                      offeringError(field)
                                        ? `offering-${field}-error`
                                        : "",
                                    ]
                                      .filter(Boolean)
                                      .join(" ") || undefined
                                  }
                                />
                                {fieldError(field)}
                              </div>
                            ))}
                            <div className="space-y-2">
                              <Label htmlFor="offering-tax_mode">
                                {t("services.tax_mode")}
                              </Label>
                              <Select
                                name="service_offering[tax_mode]"
                                value={
                                  offeringDraft.values.tax_mode === "inclusive"
                                    ? "inclusive"
                                    : "exclusive"
                                }
                                onValueChange={(value) => {
                                  if (value)
                                    offeringDraft.change("tax_mode", value);
                                }}
                                items={{
                                  inclusive: t("services.inclusive"),
                                  exclusive: t("services.exclusive"),
                                }}
                              >
                                <SelectTrigger
                                  id="offering-tax_mode"
                                  className="w-full"
                                  aria-invalid={!!errors?.tax_mode}
                                  aria-describedby={
                                    errors?.tax_mode
                                      ? "offering-tax_mode-error"
                                      : undefined
                                  }
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="exclusive">
                                    {t("services.exclusive")}
                                  </SelectItem>
                                  <SelectItem value="inclusive">
                                    {t("services.inclusive")}
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              {fieldError("tax_mode")}
                            </div>
                          </FormFields>
                          <p
                            id="offering-input-help"
                            className="text-sm text-muted-foreground"
                          >
                            {t("services.amount_help")}
                          </p>
                          <div className="flex min-h-11 items-center gap-3">
                            <Checkbox
                              id="offering-available"
                              name="service_offering[available]"
                              value="1"
                              uncheckedValue="0"
                              checked={offeringDraft.values.available !== "0"}
                              onCheckedChange={(checked) =>
                                offeringDraft.change(
                                  "available",
                                  checked ? "1" : "0",
                                )
                              }
                            />
                            <Label htmlFor="offering-available">
                              {t("services.available")}
                            </Label>
                          </div>
                          {fieldError("available")}
                        </fieldset>
                      </FieldSet>
                    </FieldGroup>
                  </fieldset>
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" disabled={processing}>
                      {t("services.save")}
                    </Button>
                    <Button
                      variant="outline"
                      disabled={processing}
                      onClick={discard}
                      render={
                        <Link
                          href={
                            booking_return?.path ||
                            locationServicesPath(locationId)
                          }
                        />
                      }
                    >
                      {t("customers.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </Form>
            {service && !service.archived && (
              <section className="space-y-3 border-t pt-6">
                <h2 className="text-section font-semibold">
                  {t("services.archive")}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t("services.archive_help")}
                </p>
                <Form
                  action={locationServiceArchivalPath(locationId, service.id)}
                  method="post"
                  onSuccess={discard}
                >
                  {({ processing }) => (
                    <>
                      <BookingReturnField context={booking_return} />
                      <input
                        type="hidden"
                        name="revision"
                        value={service.revision}
                      />
                      <ConfirmSubmitButton
                        title={t("services.archive_title", {
                          service: service.name,
                        })}
                        description={t("services.archive_help")}
                        confirmLabel={t("services.archive")}
                        cancelLabel={t("customers.cancel")}
                        processing={processing}
                      >
                        {t("services.archive")}
                      </ConfirmSubmitButton>
                    </>
                  )}
                </Form>
              </section>
            )}
          </FormContent>
        </CaseWorkspace>
      </PageCanvas>
    </AppLayout>
  );
}
